import React, { useRef, useEffect, useState } from "react";
import {
  Volume2,
  VolumeX,
  SkipForward,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import { cn } from "../../../lib/utils";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../../components/ui/Card";
import { Button } from "../../../components/ui/Button";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "../../../components/ui/Alert";
// Import the loading context
import { useLoading } from "../../../contexts/LoadingContext";

interface TutorialTooltipProps {
  targetSelector: string;
  title: string;
  content: React.ReactNode;
  position?: "top" | "right" | "bottom" | "left";
  onNext: () => void;
  onPrev: () => void;
  onClose: () => void;
  onSkipAudio: () => void;
  showPrev: boolean;
  showNext: boolean;
  showClose: boolean;
  isLastStep: boolean;
  isAudioPlaying: boolean;
  isAwaitingAction: boolean;
  requiredAction?: string;
  canProceed: boolean;
}

const TutorialTooltip: React.FC<TutorialTooltipProps> = ({
  targetSelector,
  title,
  content,
  position = "bottom",
  onNext,
  onPrev,
  onClose,
  onSkipAudio,
  showPrev,
  showNext,
  showClose,
  isLastStep,
  isAudioPlaying,
  isAwaitingAction,
  requiredAction,
  canProceed,
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const [arrowStyle, setArrowStyle] = useState<React.CSSProperties>({});
  // Add a separate state for visibility to control rendering sequence
  const [isPositioned, setIsPositioned] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  // Get loading state
  const { isLoading } = useLoading();

  // Check if this is the interactive microphone step
  const isMicrophoneInteractive =
    requiredAction === "microphone" && isAwaitingAction;

  // Refs to track animation timers
  const positionTimerRef = useRef<number | null>(null);
  const visibilityTimerRef = useRef<number | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Custom wrappers for next/prev that handle microphone transitions specially
  const handleNext = () => {
    if (isMicrophoneInteractive) {
      // For transitions from microphone interactive step, we need special handling
      setIsTransitioning(true);
      setIsVisible(false);

      // Let the transition complete first, then call the actual next function
      setTimeout(() => {
        onNext();
        // Reset after a short delay
        setTimeout(() => setIsTransitioning(false), 100);
      }, 50);
    } else {
      onNext();
    }
  };

  const handlePrev = () => {
    // Similar handling for prev from problematic steps
    if (targetSelector.includes("microphone")) {
      setIsTransitioning(true);
      setIsVisible(false);

      setTimeout(() => {
        onPrev();
        setTimeout(() => setIsTransitioning(false), 100);
      }, 50);
    } else {
      onPrev();
    }
  };

  useEffect(() => {
    // Don't position or show the tooltip if we're loading or transitioning
    if (isLoading || isTransitioning) {
      setIsVisible(false);
      setIsPositioned(false);
      return () => {};
    }

    // Clear any existing timers
    if (positionTimerRef.current) {
      clearTimeout(positionTimerRef.current);
      positionTimerRef.current = null;
    }
    if (visibilityTimerRef.current) {
      clearTimeout(visibilityTimerRef.current);
      visibilityTimerRef.current = null;
    }

    // Reset states when props change to avoid position flicker
    setIsVisible(false);
    setIsPositioned(false);

    // Function to position the tooltip
    const positionTooltip = () => {
      const targetElement = document.querySelector(targetSelector);

      if (!targetElement || !tooltipRef.current) {
        console.error("Target element or tooltip not found:", targetSelector);
        return false;
      }

      // Get viewport dimensions
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const tooltipRect = tooltipRef.current.getBoundingClientRect();

      // SPECIAL HANDLING FOR MICROPHONE INTERACTIVE STEP
      if (isMicrophoneInteractive) {
        // Find the microphone button position
        const micButton = document.querySelector(
          'button[aria-label*="microphone" i], button[title*="microphone" i], button[class*="mic" i]'
        );

        if (micButton) {
          const micRect = micButton.getBoundingClientRect();

          // Position the tooltip above the microphone button with sufficient clearance
          const tooltipStyles: React.CSSProperties = {
            position: "fixed",
            left: `${micRect.left + micRect.width / 2}px`,
            transform: "translateX(-50%)",
            bottom: `${window.innerHeight - micRect.top + 20}px`, // Position above the mic button with 20px gap
            maxWidth: "400px",
            width: "calc(100% - 40px)", // Responsive width with margins
            zIndex: 9990,
          };

          // No arrow for microphone step to avoid positioning issues
          const arrowStyles: React.CSSProperties = {
            display: "none", // Hide the arrow entirely
          };

          setTooltipStyle(tooltipStyles);
          setArrowStyle(arrowStyles);
        } else {
          // Fallback if mic button not found - position at top of screen
          const tooltipStyles: React.CSSProperties = {
            position: "fixed",
            left: "50%",
            transform: "translateX(-50%)",
            top: "100px", // Position at top with margin
            maxWidth: "400px",
            width: "calc(100% - 40px)",
            zIndex: 9990,
          };

          setTooltipStyle(tooltipStyles);
          setArrowStyle({ display: "none" });
        }

        // Add a clear indicator to the microphone button
        highlightMicrophoneButton();
        return true;
      }

      // NORMAL POSITIONING FOR ALL OTHER STEPS
      const targetRect = targetElement.getBoundingClientRect();
      const margin = 12; // Space between tooltip and target

      // Calculate initial position based on requested placement
      let top: number;
      let left: number;
      let arrowTop: number;
      let arrowLeft: number;
      let arrowTransform = "rotate(45deg)";

      switch (position) {
        case "top":
          // Tooltip above the target
          top = targetRect.top - tooltipRect.height - margin;
          left = targetRect.left + targetRect.width / 2 - tooltipRect.width / 2;

          // Arrow position (bottom center of tooltip)
          arrowTop = tooltipRect.height;
          arrowLeft = tooltipRect.width / 2 - 7; // 7px is half arrow width
          break;

        case "right":
          // Tooltip to the right of target
          top = targetRect.top + targetRect.height / 2 - tooltipRect.height / 2;
          left = targetRect.right + margin;

          // Arrow position (left center of tooltip)
          arrowTop = tooltipRect.height / 2 - 7;
          arrowLeft = -7;
          break;

        case "left":
          // Tooltip to the left of target
          top = targetRect.top + targetRect.height / 2 - tooltipRect.height / 2;
          left = targetRect.left - tooltipRect.width - margin;

          // Arrow position (right center of tooltip)
          arrowTop = tooltipRect.height / 2 - 7;
          arrowLeft = tooltipRect.width;
          break;

        case "bottom":
        default:
          // Tooltip below the target
          top = targetRect.bottom + margin;
          left = targetRect.left + targetRect.width / 2 - tooltipRect.width / 2;

          // Arrow position (top center of tooltip)
          arrowTop = -7;
          arrowLeft = tooltipRect.width / 2 - 7;
          break;
      }

      // Set arrow styles after determining position to avoid shorthand/non-shorthand conflicts
      let arrowStyles: React.CSSProperties = {
        position: "absolute",
        width: "14px",
        height: "14px",
        backgroundColor: "var(--background)",
        transform: arrowTransform,
        zIndex: 10,
      };

      // Apply appropriate border styles based on position without mixing shorthand and non-shorthand
      if (position === "top") {
        arrowStyles.borderRight = "1px solid var(--border)";
        arrowStyles.borderBottom = "1px solid var(--border)";
      } else if (position === "right") {
        arrowStyles.borderBottom = "1px solid var(--border)";
        arrowStyles.borderLeft = "1px solid var(--border)";
      } else if (position === "left") {
        arrowStyles.borderTop = "1px solid var(--border)";
        arrowStyles.borderRight = "1px solid var(--border)";
      } else {
        // bottom or default
        arrowStyles.borderTop = "1px solid var(--border)";
        arrowStyles.borderLeft = "1px solid var(--border)";
      }

      // Adjust tooltip if it goes off screen
      if (left < margin) {
        // Adjust arrow if tooltip moved from original position
        const offsetChange = margin - left;
        arrowLeft -= offsetChange;
        left = margin;
      } else if (left + tooltipRect.width > viewportWidth - margin) {
        const offsetChange =
          left + tooltipRect.width - (viewportWidth - margin);
        arrowLeft += offsetChange;
        left = viewportWidth - tooltipRect.width - margin;
      }

      if (top < margin) {
        const offsetChange = margin - top;
        arrowTop -= offsetChange;
        top = margin;
      } else if (top + tooltipRect.height > viewportHeight - margin) {
        const offsetChange =
          top + tooltipRect.height - (viewportHeight - margin);
        arrowTop += offsetChange;
        top = viewportHeight - tooltipRect.height - margin;
      }

      // Set final position styles
      const tooltipStyles: React.CSSProperties = {
        position: "fixed",
        top: `${top}px`,
        left: `${left}px`,
        zIndex: 9990, // Lower than loading overlay (99999)
        maxWidth: "350px",
        // Don't set opacity or transform here - will be handled by CSS transitions
      };

      // Update styles directly to avoid race conditions
      arrowStyles.top = `${arrowTop}px`;
      arrowStyles.left = `${arrowLeft}px`;

      setTooltipStyle(tooltipStyles);
      setArrowStyle(arrowStyles);

      // Highlight the target element
      highlightTargetElement(targetElement);
      return true;
    };

    // Helper function to highlight the targeted element
    const highlightTargetElement = (targetElement: Element) => {
      (targetElement as HTMLElement).style.position = "relative";
      const highlightEl = document.createElement("div");
      highlightEl.classList.add("tutorial-target-highlight");
      highlightEl.style.position = "absolute";
      highlightEl.style.inset = "-4px";
      highlightEl.style.borderRadius = "4px";
      highlightEl.style.border = "1.5px solid hsl(var(--primary))";
      highlightEl.style.zIndex = "9980"; // Lower than tooltip and loading
      highlightEl.style.pointerEvents = "none";
      //   highlightEl.style.animation = "pulse 2s infinite";

      // Add a style for the pulse animation if not already present
      if (!document.getElementById("tutorial-highlight-style")) {
        const style = document.createElement("style");
        // style.id = "tutorial-highlight-style";
        // style.textContent = `
        //             @keyframes pulse {s
        //                 0% { box-shadow: 0 0 0 0 hsla(var(--primary) / 0.4); }
        //                 70% { box-shadow: 0 0 0 10px hsla(var(--primary) / 0); }
        //                 100% { box-shadow: 0 0 0 0 hsla(var(--primary) / 0); }
        //             }
        //         `;
        document.head.appendChild(style);
      }

      // Clear any existing highlights first
      const existingHighlight = targetElement.querySelector(
        ".tutorial-target-highlight"
      );
      if (existingHighlight) {
        existingHighlight.remove();
      }

      targetElement.appendChild(highlightEl);
    };

    // Specific function to highlight microphone for interactive step
    const highlightMicrophoneButton = () => {
      // Find the microphone button
      const micButton = document.querySelector(
        'button[aria-label*="microphone" i], button[title*="microphone" i], button[class*="mic" i]'
      );

      if (micButton) {
        // Add extra highlighting for the microphone button
        highlightTargetElement(micButton);

        // Add additional glow effect to make it super obvious
        if (!document.getElementById("mic-button-glow")) {
          const style = document.createElement("style");

          document.head.appendChild(style);
        }

        // Add enhanced highlight class
        const highlightEl = micButton.querySelector(
          ".tutorial-target-highlight"
        );
        if (highlightEl) {
          highlightEl.classList.add("mic-highlight-enhanced");
        }
      }
    };

    // Add CSS for consistent animations
    if (!document.getElementById("tooltip-animation-styles")) {
      const style = document.createElement("style");
      style.id = "tooltip-animation-styles";
      style.textContent = `
                .tooltip-hidden {
                    opacity: 0 !important;
                    visibility: hidden;
                    transition: none !important;
                }
                
                .tooltip-positioning {
                    opacity: 0;
                    visibility: visible;
                    transition: none;
                }
                
                .tooltip-visible {
                    opacity: 1;
                    visibility: visible;
                    transform: scale(1);
                    transition: opacity 0.4s ease, transform 0.4s ease;
                }
                
                .tooltip-visible.tooltip-microphone {
                    transform: translateX(-50%);
                    
                }
                
                .tooltip-positioning.tooltip-standard {
                    transform: scale(0.95);
                }
            `;
      document.head.appendChild(style);
    }

    // Two-step animation process with special handling for microphone step

    // For the microphone interactive step, we use a different positioning approach
    if (isMicrophoneInteractive) {
      // Special positioning for microphone step - find mic button and position relative to it
      positionTimerRef.current = window.setTimeout(() => {
        const micButton = document.querySelector(
          'button[aria-label*="microphone" i], button[title*="microphone" i], button[class*="mic" i]'
        );

        if (micButton) {
          const micRect = micButton.getBoundingClientRect();

          // Position tooltip above the mic button
          const topPositionStyle: React.CSSProperties = {
            position: "fixed",
            left: `${micRect.left + micRect.width / 2}px`,
            transform: "translateX(-50%)",
            bottom: `${window.innerHeight - micRect.top + 20}px`, // Position above with gap
            maxWidth: "400px",
            width: "calc(100% - 40px)",
            zIndex: 9990,
          };

          setTooltipStyle(topPositionStyle);
        } else {
          // Fallback - position at top of screen if mic button not found
          const topPositionStyle: React.CSSProperties = {
            position: "fixed",
            left: "50%",
            transform: "translateX(-50%)",
            top: "100px",
            maxWidth: "400px",
            width: "calc(100% - 40px)",
            zIndex: 9990,
          };

          setTooltipStyle(topPositionStyle);
        }

        setArrowStyle({ display: "none" });

        // Still highlight the microphone button
        highlightMicrophoneButton();

        // Mark as positioned
        setIsPositioned(true);

        // Then show it
        visibilityTimerRef.current = window.setTimeout(() => {
          setIsVisible(true);
        }, 50);
      }, 100);
    } else {
      // Normal two-step process for other steps
      positionTimerRef.current = window.setTimeout(() => {
        const positioned = positionTooltip();
        if (positioned) {
          setIsPositioned(true);

          // Step 2: Show tooltip with animation after position is set
          visibilityTimerRef.current = window.setTimeout(() => {
            setIsVisible(true);
          }, 50); // Small delay to ensure DOM updates
        }
      }, 100);
    }

    // Reposition on window resize
    const handleResize = () => {
      setIsVisible(false); // Hide during resize
      setTimeout(() => {
        positionTooltip();
        setIsVisible(true); // Show after repositioning
      }, 100);
    };

    window.addEventListener("resize", handleResize);

    // Cleanup function
    return () => {
      if (positionTimerRef.current) clearTimeout(positionTimerRef.current);
      if (visibilityTimerRef.current) clearTimeout(visibilityTimerRef.current);
      window.removeEventListener("resize", handleResize);

      // Remove highlight from target element
      const targetElement = document.querySelector(
        targetSelector
      ) as HTMLElement;
      if (targetElement) {
        const highlight = targetElement.querySelector(
          ".tutorial-target-highlight"
        );
        if (highlight) {
          highlight.remove();
        }
      }

      // Also remove microphone highlight if it exists
      const micButton = document.querySelector(
        'button[aria-label*="microphone" i], button[title*="microphone" i], button[class*="mic" i]'
      );
      if (micButton) {
        const highlight = micButton.querySelector(".tutorial-target-highlight");
        if (highlight) {
          highlight.remove();
        }
      }
    };
  }, [targetSelector, position, isLoading, isMicrophoneInteractive]);

  // Don't render at all if loading
  if (isLoading) return null;

  return (
    <div
      ref={tooltipRef}
      style={tooltipStyle}
      className={cn(
        "tutorial-tooltip",
        isTransitioning ? "tooltip-transitioning" : "",
        !isPositioned ? "tooltip-hidden" : "tooltip-positioning",
        isVisible ? "tooltip-visible" : "",
        isMicrophoneInteractive ? "tooltip-microphone" : "tooltip-standard"
      )}
    >
      {/* Arrow - won't show for microphone step */}
      <div style={arrowStyle}></div>

      <Card className="shadow-lg border-border max-w-md w-full">
        <CardHeader className="p-4 pb-2 space-y-0">
          <div className="flex justify-between items-center">
            <CardTitle className="text-xl font-semibold">{title}</CardTitle>
            {/* {isAudioPlaying && (
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={onSkipAudio}
                                className="h-9 w-9 text-primary"
                                title="Skip audio">
                                <SkipForward size={18} />
                            </Button>
                        )} */}
          </div>
        </CardHeader>

        <CardContent className="p-4 pt-2 space-y-3">
          <div className="text-muted-foreground text-base">{content}</div>

          {requiredAction && (
            <Alert
              variant="default"
              className="bg-primary/10 border-primary/50"
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-primary"></span>
                </span>
                <AlertTitle className="text-sm font-semibold text-primary">
                  Action Required
                </AlertTitle>
              </div>
              <AlertDescription className="text-sm text-primary/80">
                Please{" "}
                {requiredAction === "microphone"
                  ? "click the microphone button"
                  : "complete the required action"}{" "}
                to continue
              </AlertDescription>
            </Alert>
          )}

          {/* Audio status indicator */}
          {(isAudioPlaying || !isAudioPlaying) && (
            <div className="flex items-center text-xs text-muted-foreground mt-2 pt-2 border-t border-border">
              {isAudioPlaying ? (
                <div className="flex items-center text-primary">
                  <Volume2 size={14} className="mr-2" />
                  <span className="mr-2">Audio playing</span>
                  <div className="flex items-center h-3 space-x-0.5">
                    <span className="w-0.5 h-2 bg-primary rounded "></span>
                    <span
                      className="w-0.5 h-3 bg-primary rounded "
                      style={{
                        animationDelay: "0.2s",
                      }}
                    ></span>
                    <span
                      className="w-0.5 h-2 bg-primary rounded "
                      style={{
                        animationDelay: "0.4s",
                      }}
                    ></span>
                    <span
                      className="w-0.5 h-1.5 bg-primary rounded "
                      style={{
                        animationDelay: "0.6s",
                      }}
                    ></span>
                  </div>
                </div>
              ) : (
                <div className="flex items-center">
                  <VolumeX size={14} className="mr-2" />
                  <span>Audio finished playing</span>
                </div>
              )}
            </div>
          )}
        </CardContent>

        <CardFooter className="p-4 pt-2 flex justify-between items-center">
          <div className="flex gap-2">
            {showPrev && (
              <Button
                variant="outline"
                size="default"
                onClick={handlePrev}
                disabled={isAudioPlaying && !canProceed}
                className="h-10 text-sm font-medium"
              >
                <ChevronLeft size={16} className="mr-1.5" />
                Previous
              </Button>
            )}

            {/* {showClose && !isLastStep && (
                            <Button
                                variant="ghost"
                                size="default"
                                onClick={onClose}
                                className="h-10 text-sm font-medium">
                                {isLastStep ? (
                                    "Start Interview"
                                ) : (
                                    <>
                                        <X size={16} className="mr-1.5" />
                                        Skip
                                    </>
                                )}
                            </Button>
                        )} */}
          </div>

          {showNext && (
            <Button
              variant="default"
              size="default"
              onClick={handleNext}
              disabled={!canProceed}
              className="h-10 text-sm font-medium px-5"
            >
              {isLastStep ? "Start Interview" : "Next"}
              <ChevronRight size={16} className="ml-1.5" />
            </Button>
          )}
        </CardFooter>
      </Card>
    </div>
  );
};

export default TutorialTooltip;
